import { createTheme } from '@mui/material/styles';

// Saheeh Admin Dark Theme — Matching POS Liefersoft-style
export const minimalistTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#00C896',
      light: '#FFD700',
      dark: '#7bc912',
      contrastText: '#111111',
    },
    secondary: {
      main: '#252838',
      light: '#2e3148',
      dark: '#1e2139',
      contrastText: '#e0e0e0',
    },
    background: {
      default: '#1a1d2e',
      paper: '#252838',
    },
    text: {
      primary: '#e0e0e0',
      secondary: '#9ca3af',
    },
    grey: {
      50: '#2e3148',
      100: '#2a2d3e',
      200: '#3a3d52',
      300: '#4a4d62',
      400: '#6b7280',
      500: '#9ca3af',
      600: '#b0b8c4',
      700: '#d0d0d0',
      800: '#e0e0e0',
      900: '#ffffff',
    },
    divider: '#3a3d52',
    error: {
      main: '#F44336',
      light: '#ef5350',
      dark: '#c62828',
    },
    warning: {
      main: '#FF9800',
      light: '#ffb74d',
      dark: '#e65100',
    },
    info: {
      main: '#2196F3',
      light: '#64b5f6',
      dark: '#1565c0',
    },
    success: {
      main: '#00C896',
      light: '#FFD700',
      dark: '#7bc912',
      contrastText: '#111111',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2, color: '#ffffff' },
    h2: { fontSize: '2rem', fontWeight: 600, lineHeight: 1.3, color: '#ffffff' },
    h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.3, color: '#ffffff' },
    h4: { fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.4, color: '#ffffff' },
    h5: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.4, color: '#e0e0e0' },
    h6: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.5, color: '#e0e0e0' },
    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5, color: '#e0e0e0' },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5, color: '#9ca3af' },
    button: { fontSize: '0.875rem', fontWeight: 500, textTransform: 'none' as const },
    caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4, color: '#9ca3af' },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          backgroundColor: '#1a1d2e',
          color: '#e0e0e0',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none' as const,
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)' },
        },
        contained: {
          backgroundColor: '#00C896',
          color: '#1a1d2e',
          '&:hover': { backgroundColor: '#7bc912' },
        },
        outlined: {
          borderColor: '#3a3d52',
          color: '#e0e0e0',
          '&:hover': { backgroundColor: 'rgba(0, 200, 150, 0.08)', borderColor: '#00C896' },
        },
        text: {
          color: '#e0e0e0',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#252838',
          border: '1px solid #3a3d52',
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          '&:hover': { boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#252838',
          border: '1px solid #3a3d52',
          backgroundImage: 'none',
        },
        elevation1: { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' },
        elevation2: { boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e2139',
          color: '#e0e0e0',
          borderBottom: '1px solid #3a3d52',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#0A2540',
          borderRight: '1px solid #1e2139',
          boxShadow: 'none',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 200, 150, 0.12)',
            color: '#00C896',
            '&:hover': { backgroundColor: 'rgba(0, 200, 150, 0.18)' },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: { root: { color: 'inherit', minWidth: 40 } },
    },
    MuiListItemText: {
      styleOverrides: { primary: { fontWeight: 500 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { backgroundColor: '#2d3048', color: '#e0e0e0', border: '1px solid #3a3d52' },
        colorPrimary: { backgroundColor: '#00C896', color: '#1a1d2e' },
        colorSecondary: { backgroundColor: '#252838', color: '#e0e0e0', border: '1px solid #3a3d52' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#1e2139',
            color: '#e0e0e0',
            '& fieldset': { borderColor: '#3a3d52' },
            '&:hover fieldset': { borderColor: '#00C896' },
            '&.Mui-focused fieldset': { borderColor: '#00C896' },
          },
          '& .MuiInputLabel-root': { color: '#9ca3af' },
          '& .MuiInputLabel-root.Mui-focused': { color: '#00C896' },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { backgroundColor: '#1e2139', color: '#e0e0e0' },
        icon: { color: '#9ca3af' },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: { backgroundColor: '#252838', border: '1px solid #3a3d52' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#e0e0e0',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
          '&.Mui-selected': { backgroundColor: 'rgba(0, 200, 150, 0.12)', color: '#00C896' },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1d2e',
          '& .MuiTableCell-head': { fontWeight: 600, color: '#e0e0e0', borderBottom: '2px solid #3a3d52' },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: 'rgba(0, 200, 150, 0.04)' } },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: '1px solid #2a2d3e', color: '#e0e0e0' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { color: '#9ca3af', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' } },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { backgroundColor: '#00C896', color: '#1a1d2e' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: '#252838', border: '1px solid #3a3d52', backgroundImage: 'none' },
      },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { color: '#ffffff' } },
    },
    MuiDialogContent: {
      styleOverrides: { root: { color: '#e0e0e0' } },
    },
    MuiTab: {
      styleOverrides: {
        root: { color: '#9ca3af', '&.Mui-selected': { color: '#00C896' } },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#00C896' },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: '#00C896' },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00C896' },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: '#333654', color: '#e0e0e0' },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: '#3a3d52' } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
        standardSuccess: { backgroundColor: 'rgba(0, 200, 150, 0.1)', color: '#00C896' },
        standardError: { backgroundColor: 'rgba(244, 67, 54, 0.1)', color: '#F44336' },
        standardWarning: { backgroundColor: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' },
        standardInfo: { backgroundColor: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: '#2d3048' },
        bar: { backgroundColor: '#00C896' },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: { color: '#00C896' },
      },
    },
  },
});

export default minimalistTheme;
